import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './WalletConnectModal.css';

// 检测是否为移动设备
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// 获取当前页面 URL（用于深度链接）
const getCurrentUrl = () => {
  return encodeURIComponent(window.location.href);
};

const WalletConnectModal = ({ isOpen, onClose, onConnect }) => {
  const { t } = useTranslation();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  // 获取可用的钱包 provider
  const provider = useMemo(() => {
    if (typeof window !== 'undefined') {
      // OKX 钱包优先检测 okxwallet
      if (window.okxwallet) return window.okxwallet;
      // MetaMask
      if (window.ethereum && window.ethereum.isMetaMask) return window.ethereum;
      // BitKeep
      if (window.bitkeep && window.bitkeep.ethereum) return window.bitkeep.ethereum;
      // Binance Chain Wallet
      if (window.BinanceChain) return window.BinanceChain;
      // 通用 ethereum provider
      if (window.ethereum) return window.ethereum;
    }
    return null;
  }, []);

  // TronLink 检测（波场链）
  const tronProvider = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (window.tronWeb || window.tronLink) return window.tronWeb || window.tronLink;
    }
    return null;
  }, []);

  const hasAnyProvider = !!provider || !!tronProvider;

  // 手机端打开钱包 App 的深度链接
  const openInWalletApp = (walletType) => {
    const currentUrl = getCurrentUrl();
    const host = window.location.host;
    const path = window.location.pathname;
    let deepLink = '';
    
    switch (walletType) {
      case 'metamask':
        // MetaMask 深度链接
        deepLink = `https://metamask.app.link/dapp/${host}${path}`;
        break;
      case 'okx':
        // OKX 深度链接
        deepLink = `okx://wallet/dapp/details?dappUrl=${currentUrl}`;
        break;
      case 'bitkeep':
        // BitKeep/Bitget 深度链接
        deepLink = `https://bkcode.vip/dapp?url=${currentUrl}`;
        break;
      case 'trust':
        // Trust Wallet 深度链接
        deepLink = `https://link.trustwallet.com/open_url?coin_id=60&url=${currentUrl}`;
        break;
      case 'tokenpocket':
        // TokenPocket 深度链接
        deepLink = `tpdapp://open?params={"url":"${window.location.href}"}`;
        break;
      case 'imtoken':
        // imToken 深度链接
        deepLink = `imtokenv2://navigate/DappView?url=${currentUrl}`;
        break;
      case 'coinbase':
        // Coinbase Wallet 深度链接
        deepLink = `https://go.cb-w.com/dapp?cb_url=${currentUrl}`;
        break;
      case 'binance':
        // Binance Web3 Wallet 深度链接
        deepLink = `bnc://app.binance.com/cedefi/dapp-webview?url=${currentUrl}`;
        break;
      default:
        return;
    }
    
    window.location.href = deepLink;
  };

  const handleConnect = async () => {
    if (!provider) {
      setError(t('wallet.noWallet'));
      return;
    }

    try {
      setConnecting(true);
      setError('');
      
      // 调用父组件传入的连接函数
      await onConnect();
      
      // 连接成功后关闭弹窗
      onClose();
    } catch (e) {
      console.error('钱包连接失败:', e);
      if (e.code === 4001) {
        setError(t('wallet.rejected'));
      } else {
        setError(t('wallet.connectFailed') + ': ' + (e.message || t('wallet.unknownError')));
      }
    } finally {
      setConnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wallet-modal-overlay" onClick={onClose}>
      <div className="wallet-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="wallet-modal-header">
          <h2>{t('wallet.connectWallet')}</h2>
          <button className="wallet-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="wallet-modal-body">
          {error && (
            <div className="wallet-modal-error">
              {error}
            </div>
          )}
          
          <div className="wallet-modal-description">
            <p>{t('wallet.connectDesc')}</p>
            <p className="wallet-modal-hint">{t('wallet.supportedWallets')}</p>
          </div>

          <button 
            className="wallet-modal-connect-btn" 
            onClick={handleConnect}
            disabled={connecting || !hasAnyProvider}
          >
            {connecting ? t('wallet.connecting') : (hasAnyProvider ? (tronProvider ? t('wallet.connectTronLink') : t('wallet.connectWallet')) : t('wallet.noWalletDetected'))}
          </button>

          {tronProvider && !provider && (
            <div className="wallet-modal-description" style={{ marginTop: 12 }}>
              <p className="wallet-modal-hint">🦎 {t('wallet.tronLinkDetected')}</p>
            </div>
          )}

          {!hasAnyProvider && (
            <div className="wallet-modal-install">
              {isMobile() ? (
                <>
                  <p>{t('wallet.openInWalletApp')}</p>
                  <div className="wallet-modal-wallet-buttons">
                    <button 
                      className="wallet-btn wallet-btn-metamask"
                      onClick={() => openInWalletApp('metamask')}
                    >
                      🦊 MetaMask
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-okx"
                      onClick={() => openInWalletApp('okx')}
                    >
                      OKX Wallet
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-trust"
                      onClick={() => openInWalletApp('trust')}
                    >
                      🛡️ Trust Wallet
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-tokenpocket"
                      onClick={() => openInWalletApp('tokenpocket')}
                    >
                      💼 TokenPocket
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-imtoken"
                      onClick={() => openInWalletApp('imtoken')}
                    >
                      imToken
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-coinbase"
                      onClick={() => openInWalletApp('coinbase')}
                    >
                      🔵 Coinbase Wallet
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-bitkeep"
                      onClick={() => openInWalletApp('bitkeep')}
                    >
                      Bitget Wallet
                    </button>
                    <button 
                      className="wallet-btn wallet-btn-binance"
                      onClick={() => openInWalletApp('binance')}
                    >
                      🟡 Binance Web3
                    </button>
                  </div>
                  <p className="wallet-modal-mobile-hint">{t('wallet.mobileHint')}</p>
                </>
              ) : (
                <>
                  <p>{t('wallet.needInstall')}</p>
                  <div className="wallet-modal-links">
                    <a href="https://metamask.io/" target="_blank" rel="noopener noreferrer">MetaMask</a>
                    <a href="https://www.okx.com/web3" target="_blank" rel="noopener noreferrer">OKX</a>
                    <a href="https://trustwallet.com/" target="_blank" rel="noopener noreferrer">Trust</a>
                    <a href="https://www.tokenpocket.pro/" target="_blank" rel="noopener noreferrer">TokenPocket</a>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletConnectModal;
